import React, { useState, useEffect, useRef } from 'react';
import { 
  HelpCircle, 
  X, 
  ArrowRight, 
  CheckCircle2,
  Info,
  MessageCircle,
  FileText,
  Calendar,
  TrendingUp,
  Zap,
  BookOpen,
  Target,
  Sparkles,
  RefreshCw
} from 'lucide-react';

// Contenu d'aide pour chaque vue et contexte
export const WORKSPACE_HELP_CONTENT = {
  chat: {
    conversationLibre: {
      title: "Conversation libre",
      description: "Discutez naturellement avec Aladinn",
      tips: [
        "Demandez des conseils personnalisés",
        "Explorez de nouvelles idées",
        "Sauvegardez dans l'onglet Notes"
      ],
      nextAction: "Passez en Mode guidé",
      icon: MessageCircle,
      color: "blue"
    },
    progressionGuidee: {
      title: "Progression guidée",
      description: "Questions ciblées pour avancer",
      tips: [
        "3 questions essentielles",
        "Réponses à choix multiples",
        "Mise à jour auto. de l'avancement dans les notes"
      ],
      nextAction: "Consultez l'onglet Notes",
      icon: Target,
      color: "green"
    },
    general: {
      title: "Chat avec Aladinn",
      description: "Deux modes complémentaires pour avancer sur votre projet",
      tips: [
        "Mode libre : flexibilité et exploration",
        "Mode guidé : structure et progression automatique",
        "Changez de mode à tout moment avec les boutons du haut",
        "Tout est synchronisé automatiquement avec les Notes"
      ],
      nextAction: "Explorez les autres onglets pour une vue complète",
      icon: MessageCircle,
      color: "purple"
    }
  },
  notes: {
    decisions: {
      title: "Décisions et actions",
      description: "Capturez vos décisions clés et actions à entreprendre",
      tips: [
        "Alimenté automatiquement par vos conversations",
        "Modifiez directement le texte si nécessaire",
        "Organisez par thème ou chronologie",
        "Exportez pour partager avec votre équipe"
      ],
      nextAction: "Passez au Planning pour planifier vos actions",
      icon: CheckCircle2,
      color: "green"
    },
    questions: {
      title: "Questions et défis",
      description: "Identifiez et suivez vos points bloquants",
      tips: [
        "Aladinn détecte automatiquement vos questions",
        "Retournez au Chat pour résoudre un défi",
        "Marquez comme résolu une fois traité",
        "Priorisez les défis urgents"
      ],
      nextAction: "Discutez dans le Chat pour débloquer ces points",
      icon: HelpCircle,
      color: "orange"
    },
    recommendations: {
      title: "Recommandations IA",
      description: "Suggestions personnalisées basées sur votre contexte",
      tips: [
        "Générées automatiquement selon votre avancement",
        "Basées sur vos décisions et défis identifiés",
        "Actualisées après chaque conversation guidée",
        "Cliquez pour approfondir dans le Chat"
      ],
      nextAction: "Intégrez ces recommandations à votre plan",
      icon: Sparkles,
      color: "purple"
    },
    resources: {
      title: "Ressources",
      description: "Classez et organisez vos informations utiles par thème",
      tips: [
        "Sauvegarde manuelle depuis le Chat (icône de sauvegarde)",
        "Créez vos propres catégories",
        "Ajoutez liens, documents, notes personnelles",
        "Recherchez rapidement dans votre base"
      ],
      nextAction: "Utilisez ces ressources pour enrichir vos actions",
      icon: BookOpen,
      color: "blue"
    },
    general: {
      title: "",
      description: "Notes d'avancement du projet",
      tips: [
        "Consulter ou Modifiez l'avancement",
        "Mise à jour auto. par le Chat",
        "Consultez l'historique"
      ],
      nextAction: "Retournez au Chat pour enrichir vos notes",
      icon: FileText,
      color: "purple"
    }
  },
  planning: {
    title: "",
    description: "Organiser vos actions dans le temps",
    tips: [
      "planifier dans le calendrier",
      "Détailler et réviser vos actions",
      "Partager vos actions"
    ],
    nextAction: "Consultez votre progression",
    icon: Calendar,
    color: "green"
  },
  analytics: {
    title: "",
    description: "Visualisez votre avancement et vos résultats",
    tips: [
      "Tableaux de bord temps réel de votre progression",
      "Historique de vos réalisations",
      "Identifiez les points d'amélioration"
    ],
    nextAction: "Utilisez ces insights pour ajuster votre stratégie",
    icon: TrendingUp,
    color: "orange"
  }
};

// Composant Tooltip amélioré
export const WorkspaceTooltip = ({ children, content, position = 'bottom', isVisible, onClose }) => {
  const [tooltipPosition, setTooltipPosition] = useState(position);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (isVisible && tooltipRef.current) {
      const rect = tooltipRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;

      if (rect.bottom > windowHeight - 20) {
        setTooltipPosition('top');
      } else if (rect.top < 20) {
        setTooltipPosition('bottom');
      }

      if (rect.right > windowWidth - 20) {
        setTooltipPosition(prev => prev + '-left');
      }
    }
  }, [isVisible]);

  if (!isVisible) return children;

  const positionClasses = {
    'top': 'bottom-full left-1/2 transform -translate-x-1/2 mb-2',
    'bottom': 'top-full left-1/2 transform -translate-x-1/2 mt-2',
    'left': 'right-full top-1/2 transform -translate-y-1/2 mr-2',
    'right': 'left-full top-1/2 transform -translate-y-1/2 ml-2',
    'top-left': 'bottom-full right-0 mb-2',
    'bottom-left': 'top-full right-0 mt-2'
  };

  return (
    <div className="relative inline-block">
      {children}
      <div 
        ref={tooltipRef}
        className={`absolute z-50 ${positionClasses[tooltipPosition] || positionClasses.bottom}`}
      >
        <div className="bg-white border border-gray-200 rounded-xl shadow-2xl p-5 max-w-md animate-fade-in">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              {content.icon && <content.icon className={`w-5 h-5 text-${content.color}-600`} />}
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            )}
          </div>
          
          <div className="space-y-3">
            {content.description && (
              <p className="text-sm font-medium text-purple-700">{content.description}</p>
            )}
            {content.tips && (
              <ul className="space-y-2">
                {content.tips.map((tip, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">{tip}</span>
                  </li>
                ))}
              </ul>
            )}
            {content.nextAction && (
              <div className="flex items-center gap-2 text-blue-600 font-medium pt-2 border-t border-gray-200">
                <ArrowRight className="w-4 h-4" />
                <span className="text-sm">{content.nextAction}</span>
              </div>
            )}
          </div>
        </div>
        {/* Flèche du tooltip */}
        <div className={`absolute w-3 h-3 bg-white border transform rotate-45 ${
          tooltipPosition.includes('top') ? 'top-full -mt-1.5 left-1/2 -translate-x-1/2 border-b border-r' :
          tooltipPosition.includes('bottom') ? 'bottom-full -mb-1.5 left-1/2 -translate-x-1/2 border-t border-l' :
          tooltipPosition.includes('left') ? 'left-full -ml-1.5 top-1/2 -translate-y-1/2 border-t border-r' :
          'right-full -mr-1.5 top-1/2 -translate-y-1/2 border-b border-l'
        }`} />
      </div>
    </div>
  );
};

// Hook pour gérer l'aide contextuelle
export const useWorkspaceHelp = (viewId, contextId = null) => {
  const [showHelp, setShowHelp] = useState(false);
  const [hasSeenHelp, setHasSeenHelp] = useState(false);

  useEffect(() => {
    if (showHelp) {
      setHasSeenHelp(true);
      const viewedHelp = JSON.parse(localStorage.getItem('aladinn_workspace_help_viewed') || '[]');
      const helpKey = contextId ? `${viewId}-${contextId}` : viewId;
      if (!viewedHelp.includes(helpKey)) {
        viewedHelp.push(helpKey);
        localStorage.setItem('aladinn_workspace_help_viewed', JSON.stringify(viewedHelp));
      }
    }
  }, [showHelp, viewId, contextId]);

  const toggleHelp = () => setShowHelp(!showHelp);
  const hideHelp = () => setShowHelp(false);
  
  // Récupérer le contenu d'aide approprié
  let helpContent = null;
  if (contextId && WORKSPACE_HELP_CONTENT[viewId]?.[contextId]) {
    helpContent = WORKSPACE_HELP_CONTENT[viewId][contextId];
  } else if (WORKSPACE_HELP_CONTENT[viewId]?.general) {
    helpContent = WORKSPACE_HELP_CONTENT[viewId].general;
  } else if (WORKSPACE_HELP_CONTENT[viewId]) {
    helpContent = WORKSPACE_HELP_CONTENT[viewId];
  }

  return { 
    showHelp, 
    toggleHelp, 
    hideHelp, 
    helpContent, 
    hasSeenHelp 
  };
};

// Composant bouton d'aide
export const WorkspaceHelpButton = ({ 
  viewId,
  contextId = null,
  isActive = false, 
  size = 'sm',
  variant = 'icon',
  className = "",
  title = "Aide contextuelle",
  showPulse = false
}) => {
  const { showHelp, toggleHelp, hideHelp, helpContent, hasSeenHelp } = useWorkspaceHelp(viewId, contextId);

  if (!helpContent) return null;

  const handleClose = () => {
    hideHelp();
  };

  const sizeClasses = {
    'xs': 'w-3 h-3',
    'sm': 'w-4 h-4', 
    'md': 'w-5 h-5',
    'lg': 'w-6 h-6'
  };

  const buttonSizeClasses = {
    'xs': 'p-0.5',
    'sm': 'p-1.5',
    'md': 'p-2', 
    'lg': 'p-3'
  };

  if (variant === 'text') {
    return (
      <WorkspaceTooltip
        content={helpContent}
        isVisible={showHelp}
        onClose={handleClose}
      >
        <button
          onClick={toggleHelp}
          className={`flex items-center space-x-2 px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium ${className}`}
          title={title}
        >
          <HelpCircle className="w-4 h-4" />
          <span>Aide</span>
        </button>
      </WorkspaceTooltip>
    );
  }

  return (
    <WorkspaceTooltip
      content={helpContent}
      isVisible={showHelp}
      onClose={handleClose}
    >
      <button
        onClick={toggleHelp}
        className={`relative ${buttonSizeClasses[size]} rounded-full transition-all ${
          isActive || showPulse
            ? 'bg-purple-100 text-purple-600 animate-pulse' 
            : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
        } ${className}`}
        title={title}
      >
        <HelpCircle className={sizeClasses[size]} />
        {(isActive || showPulse) && !hasSeenHelp && (
          <span className="absolute -top-1 -right-1 w-2 h-2 bg-purple-500 rounded-full animate-ping" />
        )}
      </button>
    </WorkspaceTooltip>
  );
};

// Composant bandeau d'info contextuel (apparaît au-dessus de la vue)
export const WorkspaceInfoBanner = ({ viewId, contextId, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const bannerKey = contextId ? `${viewId}-${contextId}` : viewId;
    const dismissedBanners = JSON.parse(localStorage.getItem('aladinn_workspace_banners_dismissed') || '[]');
    if (dismissedBanners.includes(bannerKey)) {
      setIsVisible(false);
    }
  }, [viewId, contextId]);

  const handleDismiss = () => {
    const bannerKey = contextId ? `${viewId}-${contextId}` : viewId;
    const dismissedBanners = JSON.parse(localStorage.getItem('aladinn_workspace_banners_dismissed') || '[]');
    dismissedBanners.push(bannerKey);
    localStorage.setItem('aladinn_workspace_banners_dismissed', JSON.stringify(dismissedBanners));
    setIsVisible(false);
    onDismiss?.();
  };

  if (!isVisible) return null;

  let helpContent = null;
  if (contextId && WORKSPACE_HELP_CONTENT[viewId]?.[contextId]) {
    helpContent = WORKSPACE_HELP_CONTENT[viewId][contextId];
  } else if (WORKSPACE_HELP_CONTENT[viewId]?.general) {
    helpContent = WORKSPACE_HELP_CONTENT[viewId].general;
  } else if (WORKSPACE_HELP_CONTENT[viewId]) {
    helpContent = WORKSPACE_HELP_CONTENT[viewId];
  }

  if (!helpContent) return null;

  const Icon = helpContent.icon;

  return (
    <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4 mb-4 animate-slide-down">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 bg-${helpContent.color}-100 rounded-lg flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-5 h-5 text-${helpContent.color}-600`} />
        </div>
        <div className="flex-1">
          <div className="flex items-start justify-between mb-2">
            <h3 className="font-semibold text-gray-900">{helpContent.title}</h3>
            <button
              onClick={handleDismiss}
              className="p-1 hover:bg-white/50 rounded-full transition-colors"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>
          <p className="text-sm text-gray-700 mb-2">{helpContent.description}</p>
          {helpContent.tips && helpContent.tips.length > 0 && (
            <div className="flex items-center gap-2 text-xs text-blue-700">
              <Info className="w-4 h-4" />
              <span>{helpContent.tips[0]}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Composant indicateur de mode (pour Chat)
export const ChatModeIndicator = ({ mode, onToggleHelp }) => {
  const isGuidedMode = mode === 'guided';
  const helpContent = isGuidedMode 
    ? WORKSPACE_HELP_CONTENT.chat.progressionGuidee 
    : WORKSPACE_HELP_CONTENT.chat.conversationLibre;

  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 ${
      isGuidedMode 
        ? 'bg-green-50 border-green-300' 
        : 'bg-blue-50 border-blue-300'
    }`}>
      <div className={`w-2 h-2 rounded-full ${
        isGuidedMode ? 'bg-green-500' : 'bg-blue-500'
      } animate-pulse`} />
      <span className="text-sm font-medium text-gray-800">
        {isGuidedMode ? 'Mode guidé actif' : 'Mode libre actif'}
      </span>
      <button
        onClick={onToggleHelp}
        className="ml-auto p-1 hover:bg-white rounded-full transition-colors"
        title="En savoir plus"
      >
        <HelpCircle className="w-4 h-4 text-gray-600" />
      </button>
    </div>
  );
};

// Composant d'aide flottante (FAB - Floating Action Button)
export const WorkspaceHelpFAB = ({ viewId, contextId, className = "" }) => {
  const { showHelp, toggleHelp, hideHelp, helpContent } = useWorkspaceHelp(viewId, contextId);

  if (!helpContent) return null;

  return (
    <div className={`fixed bottom-6 right-6 z-40 ${className}`}>
      <WorkspaceTooltip
        content={helpContent}
        isVisible={showHelp}
        onClose={hideHelp}
        position="top-left"
      >
        <button
          onClick={toggleHelp}
          className="w-14 h-14 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 flex items-center justify-center"
          title="Aide contextuelle"
        >
          <HelpCircle className="w-6 h-6" />
        </button>
      </WorkspaceTooltip>
    </div>
  );
};

// Composant de guide rapide intégré (sidebar ou panel)
export const WorkspaceQuickGuide = ({ viewId, contextId, onClose }) => {
  let helpContent = null;
  if (contextId && WORKSPACE_HELP_CONTENT[viewId]?.[contextId]) {
    helpContent = WORKSPACE_HELP_CONTENT[viewId][contextId];
  } else if (WORKSPACE_HELP_CONTENT[viewId]?.general) {
    helpContent = WORKSPACE_HELP_CONTENT[viewId].general;
  } else if (WORKSPACE_HELP_CONTENT[viewId]) {
    helpContent = WORKSPACE_HELP_CONTENT[viewId];
  }

  if (!helpContent) return null;

  const Icon = helpContent.icon;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 bg-${helpContent.color}-100 rounded-lg flex items-center justify-center`}>
            <Icon className={`w-4 h-4 text-${helpContent.color}-600`} />
          </div>
          <h3 className="font-semibold text-gray-900 text-sm">{helpContent.title}</h3>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        )}
      </div>

      <p className="text-xs text-gray-700 mb-3">{helpContent.description}</p>

      {helpContent.tips && (
        <div className="space-y-2 mb-3">
          {helpContent.tips.map((tip, index) => (
            <div key={index} className="flex items-start gap-2">
              <CheckCircle2 className="w-3 h-3 text-green-500 flex-shrink-0 mt-0.5" />
              <span className="text-xs text-gray-600">{tip}</span>
            </div>
          ))}
        </div>
      )}

      {helpContent.nextAction && (
        <div className="flex items-center gap-2 pt-3 border-t border-gray-200">
          <ArrowRight className="w-3 h-3 text-blue-600" />
          <span className="text-xs text-blue-600 font-medium">{helpContent.nextAction}</span>
        </div>
      )}
    </div>
  );
};

// Styles CSS additionnels
const styles = `
  @keyframes fade-in {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes slide-down {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-fade-in {
    animation: fade-in 0.2s ease-out;
  }

  .animate-slide-down {
    animation: slide-down 0.3s ease-out;
  }
`;

// Injection des styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}

// Export par défaut
const WorkspaceHelpSystem = {
  WorkspaceTooltip,
  WorkspaceHelpButton,
  WorkspaceInfoBanner,
  ChatModeIndicator,
  WorkspaceHelpFAB,
  WorkspaceQuickGuide,
  useWorkspaceHelp,
  WORKSPACE_HELP_CONTENT
};

export default WorkspaceHelpSystem;